import React, { useState, useRef, useEffect } from "react";
import { Button, Col, Container, Row, Card, Form } from "react-bootstrap";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import jwt_decode from "jwt-decode";
import DataApi from "../api/dataApi";
import { API_BASE_URL } from "../constants/CONSTANT";
import { COUNTRY_CODES } from "../constants/COUNTRY_CODES";
import PubSub from "pubsub-js";

const EditProfile = () => {
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState({
    firstname: "",
    lastname: "",
    email: "",
    country_code: "",
    phone: "",
    profile_image: "",
  });

  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [imageVersion, setImageVersion] = useState(0); // For cache busting

  const getUploadFileUrl = (filePath) => {
    if (!filePath || typeof filePath !== "string") return "";

    const trimmedPath = filePath.trim();
    if (!trimmedPath) return "";

    if (trimmedPath.startsWith("http://") || trimmedPath.startsWith("https://") || trimmedPath.startsWith("blob:")) {
      return trimmedPath;
    }

    const normalizedPath = trimmedPath.startsWith("/") ? trimmedPath : `/${trimmedPath}`;
    if (normalizedPath.startsWith("/uploads/")) {
      const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
      return `${apiOrigin}${normalizedPath}`;
    }

    return normalizedPath;
  };

  const getImageUrl = () => {
    if (selectedProfileImage && imagePreview) return imagePreview;

    if (profile?.profile_image) {
      const imageUrl = getUploadFileUrl(profile.profile_image);
      return imageVersion ? `${imageUrl}?v=${imageVersion}` : imageUrl;
    }

    return null;
  };

  /* ================= FETCH USER ================= */
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const token = sessionStorage.getItem("token");
        if (!token) return;

        const decoded = jwt_decode(token);
        setUserId(decoded.id);

        const userApi = new DataApi("user");
        const res = await userApi.fetchById(decoded.id);

        console.log("Fetched user profile:", res.data.profile_image);

        setProfile(res.data);
        
        // Removed the imagePreview set here to avoid conflict between API paths and local blobs
        // We will handle the API_BASE_URL directly in the JSX
      } catch (error) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: "Failed to load profile",
        });
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Only JPG, PNG, and GIF images are allowed",
      });
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Image size must be less than 5MB",
      });
      e.target.value = "";
      return;
    }

    setSelectedProfileImage(file);
    setImagePreview(URL.createObjectURL(file)); // Sets a local 'blob:' URL for immediate preview
    setImageError(false);
  };

  /* ================= SAVE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const userApi = new DataApi("user");

      if (selectedProfileImage) {
        // --- BRANCH 1: UPDATING IMAGE & TEXT ---
        const formData = new FormData();
        formData.append("profile_image", selectedProfileImage);

        Object.keys(profile).forEach((key) => {
          if (key !== "profile_image" && key !== "password" && profile[key] !== null) {
            formData.append(key, profile[key]);
          }
        });

        const response = await userApi.updateFormData(formData, userId);

        if (response.data?.success) {
          const updatedUser = response.data.data;
          setProfile(updatedUser);
          setSelectedProfileImage(null);
          setImagePreview(""); // Clear local blob preview, let it fall back to updated DB image
          setImageError(false);
          setImageVersion(Date.now());

          PubSub.publish("RECORD_SAVED_TOAST", {
            title: "Success",
            message: "Profile updated successfully!",
          });

          // Trigger dashboard and header updates
          window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: updatedUser }));
          PubSub.publish("USER_UPDATED", { user: updatedUser });
        }
      } else {
        // --- BRANCH 2: UPDATING TEXT ONLY ---
        const { password, ...safeProfile } = profile;
        const response = await userApi.update(safeProfile, userId);

        if (response.data) {
          const updatedUser = response.data.data || response.data;
          setProfile(updatedUser);

          PubSub.publish("RECORD_SAVED_TOAST", {
            title: "Success",
            message: "Profile updated successfully!",
          });

          // 🔴 FIX: Added missing PubSub trigger so Header/Dashboard know about text-only changes
          window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: updatedUser }));
          PubSub.publish("USER_UPDATED", { user: updatedUser });
        }
      }
    } catch (error) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: error.response?.data?.errors || "Failed to update profile details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <div>Loading...</div>;

 

  return (
    <Container fluid className="py-4">
      <Row>
        <Col lg={4}>
          <Card className="border-0 shadow-sm text-center">
            <Card.Body>
            <div className="position-relative d-inline-block mb-3">
            {getImageUrl() && !imageError ? (
              <img
                src={getImageUrl()}
                alt="Profile"
                className="rounded-circle shadow-sm"
                style={{
                  width: "150px",
                  height: "150px",
                  objectFit: "cover",
                  border: "4px solid #fff",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
                }}
                onError={(e) => {
                  console.error("Image load failed for URL:", e.target.src);
                  setImageError(true);
                }}
              />
            ) : (
              // Fallback: Agar image nahi hai ya load nahi hui toh Initials dikhayenge
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mx-auto shadow-sm"
                style={{
                  width: "150px",
                  height: "150px",
                  background: "#1e3a8a", // Dark Blue color
                  color: "white",
                  fontWeight: "600",
                  fontSize: "48px",
                  border: "4px solid #fff"
                }}
              >
                {/* Muskan Khan -> MK */}
                {(profile.firstname?.charAt(0) || "") + (profile.lastname?.charAt(0) || "")}
              </div>
            )}

                {/* Camera Icon Overlay */}
              <div
                className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center shadow"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "#0d6efd",
                  color: "white",
                  cursor: "pointer",
                  border: "3px solid white"
                }}
                onClick={() => fileInputRef.current.click()}
              >
                <i className="fa-solid fa-camera"></i>
              </div>
            </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handlePhotoUpload}
              />

              <h4 className="mt-3">
                {profile.firstname} {profile.lastname}
              </h4>
              <p className="text-muted">{profile.email}</p>
            </Card.Body>
          </Card>
        </Col>

        {/* ===== FORM ===== */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Label>
                      First Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="firstname"
                      value={profile.firstname || ""}
                      onChange={handleChange}
                      required
                    />
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Label>
                      Last Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="lastname"
                      value={profile.lastname || ""}
                      onChange={handleChange}
                      required
                    />
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={profile.email || ""}
                      onChange={handleChange}
                      required
                    />
                  </Col>

                  <Col md={3} className="mb-3">
                    <Form.Label>
                      Country Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="country_code"
                      value={profile.country_code || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      {COUNTRY_CODES.map((c, i) => (
                        <option key={i} value={c.country_code}>
                          {c.country_code}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={3} className="mb-3">
                    <Form.Label>
                      Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="phone"
                      value={profile.phone || ""}
                      onChange={handleChange}
                      maxLength={15}
                      required
                    />
                  </Col>

                  <Col xs={12} className="text-end mt-3">
                    <Button className="btn-primary" type="submit">
                      Save Changes
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EditProfile;
